import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { arSA } from "date-fns/locale";
import { Check, ArrowLeft } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Reveal } from "./Reveal";
import { createBooking, getBookedSlots } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const SLOTS = ["10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00"];

export const Booking = () => {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { copy, lang } = useLang();
  const b = copy.booking;
  const dateLocale = lang === "ar" ? arSA : undefined;

  useEffect(() => {
    if (!date) return;
    setTime(null);
    getBookedSlots(format(date, "yyyy-MM-dd"))
      .then((r) => setBooked(r.data.booked || []))
      .catch(() => setBooked([]));
  }, [date]);

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = b.errName;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = b.errEmail;
    if (!/^[+\d][\d\s-]{6,}$/.test(form.phone.trim())) e.phone = b.errPhone;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await createBooking({ ...form, date: format(date, "yyyy-MM-dd"), time });
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 409) {
        setErrors({ submit: b.errConflict });
        setTime(null);
        getBookedSlots(format(date, "yyyy-MM-dd")).then((r) => setBooked(r.data.booked || []));
      } else {
        setErrors({ submit: b.errSubmit });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (err) =>
    `w-full bg-transparent border ${err ? "border-wine" : "border-plum/25"} focus:border-gold-dark outline-none px-5 py-4 text-sm font-light tracking-wide placeholder:text-plum/35 transition-colors duration-300`;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 60);

  return (
    <section id="booking" data-testid="booking-section" className="border-t hairline py-16 md:py-24 px-6 md:px-12 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-5">
          <p className="overline-label mb-6">{b.overline}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            {b.titleA}<br />
            <span className="italic text-gold-dark">{b.titleItalic}</span>
          </h2>
          <p className="mt-8 text-sm md:text-base font-light text-plum/70 leading-relaxed max-w-md">
            {b.copy}
          </p>
          <div className="mt-10 border-t hairline pt-8 flex flex-col gap-4">
            {b.steps.map((s, i) => (
              <div key={s} className="flex items-center gap-4">
                <span className="font-serif text-xl text-gold-dark">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[11px] uppercase tracking-[0.3em] text-plum/60">{s}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="border hairline p-6 md:p-10 min-h-[520px]">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="done"
                  data-testid="booking-success"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="h-full flex flex-col items-center justify-center text-center py-16"
                >
                  <span className="inline-flex items-center justify-center w-14 h-14 border border-gold-dark rounded-full mb-8">
                    <Check size={22} strokeWidth={1.25} className="text-gold-dark" />
                  </span>
                  <p className="text-[11px] uppercase tracking-[0.35em] text-plum/50 mb-4">{b.successOverline}</p>
                  <p className="font-serif text-3xl md:text-4xl">
                    {format(date, "EEEE, d MMMM", { locale: dateLocale })} <span className="italic text-gold-dark">{b.at}</span> {time}
                  </p>
                  <p className="mt-6 text-sm font-light text-plum/60 max-w-sm leading-relaxed">
                    {b.successText(form.email)}
                  </p>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.35em] text-plum/50 mb-5">{b.selectDate}</p>
                      <Calendar
                        data-testid="booking-calendar"
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={[{ before: today }, { after: maxDate }]}
                        locale={dateLocale}
                        className="p-0"
                        classNames={{
                          caption_label: "font-serif text-lg text-plum capitalize tracking-wide",
                          head_cell: "text-plum/45 uppercase tracking-[0.2em] text-[10px] font-medium w-11 rounded-none",
                          row: "flex w-full mt-1",
                          cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
                          day: "h-11 w-11 p-0 text-[13px] font-light text-plum rounded-none hover:bg-gold/40 transition-colors duration-300 aria-selected:opacity-100",
                          day_selected: "bg-plum text-cream hover:bg-plum hover:text-cream focus:bg-plum focus:text-cream rounded-none",
                          day_today: "bg-gold/30 text-plum font-medium rounded-none",
                          day_outside: "day-outside text-plum/25 aria-selected:bg-gold/20 aria-selected:text-plum/40",
                          day_disabled: "text-plum/20",
                          nav_button: "h-8 w-8 bg-transparent p-0 opacity-60 hover:opacity-100 border border-plum/20 rounded-none transition-opacity duration-300",
                        }}
                      />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.35em] text-plum/50 mb-5">
                        {date ? b.timesFor(format(date, "d MMM", { locale: dateLocale })) : b.chooseFirst}
                      </p>
                      <div className="grid grid-cols-2 gap-2.5">
                        {SLOTS.map((s) => {
                          const taken = booked.includes(s);
                          return (
                            <button
                              key={s}
                              data-testid={`booking-slot-${s.replace(":", "")}`}
                              disabled={!date || taken}
                              onClick={() => setTime(s)}
                              className={`border px-4 py-3.5 text-sm tracking-widest font-light transition-colors duration-300 ${
                                time === s
                                  ? "border-plum bg-plum text-cream"
                                  : taken
                                  ? "border-plum/10 text-plum/25 line-through cursor-not-allowed"
                                  : !date
                                  ? "border-plum/10 text-plum/25 cursor-not-allowed"
                                  : "border-plum/25 text-plum hover:border-gold-dark hover:text-gold-dark"
                              }`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>

                      <AnimatePresence>
                        {date && time && (
                          <motion.form
                            data-testid="booking-form"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            onSubmit={submit}
                            noValidate
                            className="mt-8 flex flex-col gap-3"
                          >
                            <input
                              data-testid="booking-name-input"
                              type="text"
                              placeholder={b.name}
                              value={form.name}
                              onChange={(e) => setForm({ ...form, name: e.target.value })}
                              className={inputCls(errors.name)}
                            />
                            {errors.name && <p data-testid="booking-name-error" className="text-xs text-wine">{errors.name}</p>}
                            <input
                              data-testid="booking-email-input"
                              type="email"
                              placeholder={b.email}
                              value={form.email}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              className={inputCls(errors.email)}
                            />
                            {errors.email && <p data-testid="booking-email-error" className="text-xs text-wine">{errors.email}</p>}
                            <input
                              data-testid="booking-phone-input"
                              type="tel"
                              placeholder={b.phone}
                              value={form.phone}
                              onChange={(e) => setForm({ ...form, phone: e.target.value })}
                              className={inputCls(errors.phone)}
                            />
                            {errors.phone && <p data-testid="booking-phone-error" className="text-xs text-wine">{errors.phone}</p>}
                            {errors.submit && <p data-testid="booking-submit-error" className="text-xs text-wine">{errors.submit}</p>}
                            <button
                              type="submit"
                              data-testid="booking-submit-btn"
                              disabled={submitting}
                              className="mt-2 bg-gold text-cream text-[11px] uppercase tracking-[0.25em] px-8 py-4 hover:bg-plum hover:text-cream transition-colors duration-300 disabled:opacity-50"
                            >
                              {submitting ? b.submitting : b.submit}
                            </button>
                          </motion.form>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
